import type { QuestNode, QuestShape } from './model';

export function addQuestShapePath(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  shape: QuestShape,
): void {
  ctx.beginPath();
  switch (shape) {
    case 'square':
      ctx.rect(cx - r, cy - r, r * 2, r * 2);
      break;
    case 'rsquare':
    case 'rounded_square': {
      roundedRect(ctx, cx - r, cy - r, r * 2, r * 2, r * 0.35);
      break;
    }
    case 'diamond':
      ctx.moveTo(cx, cy - r);
      ctx.lineTo(cx + r, cy);
      ctx.lineTo(cx, cy + r);
      ctx.lineTo(cx - r, cy);
      ctx.closePath();
      break;
    case 'pentagon':
      polygon(ctx, cx, cy, r, 5, -Math.PI / 2);
      break;
    case 'hexagon':
      polygon(ctx, cx, cy, r, 6, 0);
      break;
    case 'octagon':
      polygon(ctx, cx, cy, r, 8, Math.PI / 8);
      break;
    case 'heart':
      heart(ctx, cx, cy, r);
      break;
    case 'gear':
      gear(ctx, cx, cy, r);
      break;
    case 'none':
      ctx.arc(cx, cy, r * 0.15, 0, Math.PI * 2);
      break;
    case 'circle':
    default:
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      break;
  }
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  cr: number,
): void {
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(x, y, w, h, cr);
    return;
  }
  ctx.moveTo(x + cr, y);
  ctx.lineTo(x + w - cr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + cr);
  ctx.lineTo(x + w, y + h - cr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - cr, y + h);
  ctx.lineTo(x + cr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - cr);
  ctx.lineTo(x, y + cr);
  ctx.quadraticCurveTo(x, y, x + cr, y);
  ctx.closePath();
}

function polygon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  sides: number,
  start: number,
): void {
  for (let i = 0; i < sides; i++) {
    const a = start + (i * Math.PI * 2) / sides;
    const x = cx + Math.cos(a) * r;
    const y = cy + Math.sin(a) * r;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

function heart(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number): void {
  const s = r / 16;
  ctx.moveTo(cx, cy + 6 * s);
  ctx.bezierCurveTo(cx, cy + 2 * s, cx - 8 * s, cy - 2 * s, cx - 8 * s, cy - 6 * s);
  ctx.bezierCurveTo(cx - 8 * s, cy - 11 * s, cx - 2 * s, cy - 11 * s, cx, cy - 7 * s);
  ctx.bezierCurveTo(cx + 2 * s, cy - 11 * s, cx + 8 * s, cy - 11 * s, cx + 8 * s, cy - 6 * s);
  ctx.bezierCurveTo(cx + 8 * s, cy - 2 * s, cx, cy + 2 * s, cx, cy + 6 * s);
  ctx.closePath();
}

function gear(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number): void {
  const teeth = 8;
  const inner = r * 0.72;
  for (let i = 0; i < teeth * 2; i++) {
    const a = (i * Math.PI) / teeth - Math.PI / (teeth * 2);
    const rad = i % 2 === 0 ? r : inner;
    const x = cx + Math.cos(a) * rad;
    const y = cy + Math.sin(a) * rad;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

export function drawDependencyCurve(
  ctx: CanvasRenderingContext2D,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  hot: boolean,
): void {
  const mx = (ax + bx) / 2;
  const my = (ay + by) / 2 + Math.min(40, Math.abs(bx - ax) * 0.15);
  ctx.beginPath();
  ctx.moveTo(ax, ay);
  ctx.quadraticCurveTo(mx, my, bx, by);
  ctx.strokeStyle = hot ? 'rgba(200, 173, 255, 0.9)' : 'rgba(145, 94, 255, 0.32)';
  ctx.lineWidth = hot ? 2.4 : 1.4;
  ctx.stroke();

  const t = 0.86;
  const qx = (1 - t) * (1 - t) * ax + 2 * (1 - t) * t * mx + t * t * bx;
  const qy = (1 - t) * (1 - t) * ay + 2 * (1 - t) * t * my + t * t * by;
  const dx = 2 * (1 - t) * (mx - ax) + 2 * t * (bx - mx);
  const dy = 2 * (1 - t) * (my - ay) + 2 * t * (by - my);
  const ang = Math.atan2(dy, dx);
  const size = hot ? 8 : 6;
  ctx.beginPath();
  ctx.moveTo(qx, qy);
  ctx.lineTo(qx - Math.cos(ang - 0.4) * size, qy - Math.sin(ang - 0.4) * size);
  ctx.lineTo(qx - Math.cos(ang + 0.4) * size, qy - Math.sin(ang + 0.4) * size);
  ctx.closePath();
  ctx.fillStyle = hot ? 'rgba(200, 173, 255, 0.95)' : 'rgba(172, 133, 255, 0.55)';
  ctx.fill();
}

export function questFillColor(quest: QuestNode, hovered: boolean, selected: boolean, related: boolean): string {
  if (selected) return 'rgba(125, 69, 255, 0.72)';
  if (hovered) return 'rgba(172, 133, 255, 0.58)';
  if (related) return 'rgba(145, 94, 255, 0.42)';
  if (quest.optional) return 'rgba(56, 189, 248, 0.38)';
  return 'rgba(12, 5, 24, 0.82)';
}

export function questStrokeColor(quest: QuestNode, hovered: boolean, selected: boolean): string {
  if (selected) return 'rgba(227, 214, 255, 0.95)';
  if (hovered) return 'rgba(200, 173, 255, 0.9)';
  if (quest.optional) return 'rgba(125, 211, 252, 0.85)';
  return 'rgba(251, 191, 36, 0.78)';
}
